import { Injectable } from "@angular/core";

@Injectable({
  providedIn: "root",
})
export class CompareService {
  constructor() {}

    /**
   * Compares two values for sorting.
   *
   * The function can handle numbers, strings, null, or undefined values.
   * It returns:
   * - 0 if both values are null/undefined or equal.
   * - A negative number if 'a' should come before 'b'
   * - A positive number if 'a' should come after 'b'
   *
   * If both values are strings, it uses localeCompare with the Czech locale ("cs")
   * to properly order special characters. For numbers, standard numeric comparison is used.
   *
   * @param {string | number | null | undefined} a - The first value to compare.
   * @param {string | number | null | undefined} b - The second value to compare.
   * @param {boolean} isAsc - Determines if the sort order is ascending (true) or descending (false).
   * @returns {number} The comparison result suitable for use in a sort function.
   */
  compare(
    a: string | number | null | undefined,
    b: string | number | null | undefined,
    isAsc: boolean
  ): number {
    // If both values are null or undefined, they are considered equal.
    if (!a && !b) {
      return 0;
    }
    // If only 'a' is null or undefined, it should come before or after based on sort order.
    if (!a) {
      return isAsc ? -1 : 1;
    }
    // If only 'b' is null or undefined, it should come after or before based on sort order.
    if (!b) {
      return isAsc ? 1 : -1;
    }
    // If both values are strings, perform a locale-sensitive comparison using Czech locale.
    if (typeof a === "string" && typeof b === "string") {
      return a.localeCompare(b, "cs") * (isAsc ? 1 : -1);
    } else {
      // For number comparison, return -1 if a is less than b, else 1, then multiply by order factor.
      return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
    }
  }
}
